import { DatasourceTableJobStatus } from '@y42/datasource';
import { Y42FeatureTypeToChildLogo } from '@y42/feature';
import { deletedEntityIcon } from '@y42/feature/icons';
import { hasDiagramDarkMode } from '@y42/gojs';
import { JobStatus, Y42FeatureType } from '@y42/json-schema';
import { Binding, GraphObject, Node, Part, Point } from 'gojs';
import {
  isOrchestrationDiagramEditable,
  NodesWithIncrementalImport,
  OrchestrationDiagramMode,
  OrchestrationDiagramModelProperties,
  OrchestrationNodeBindings,
  OrchestrationUINode,
} from '../../../../models/orchestration-diagram.model';
import { orchestrationDiagramConfig } from '../../../config/orchestration-diagram.config';
import { getOrchestrationDerivedNode, getOrchestrationDiagramModelProperty } from '../../../utils/diagram-utils';
import { OrchestrationNodeContextMenu } from '../../shared/parts/node-context-menu';

export const nodeLocationBinding = () => new Binding('location', 'location', Point.parse).makeTwoWay(Point.stringify);

export const nodeDeleteableBinding = () =>
  new Binding('deletable', OrchestrationDiagramModelProperties.DiagramMode, (mode: OrchestrationDiagramMode) =>
    isOrchestrationDiagramEditable(mode),
  ).ofModel();

export const contextMenuBinding = () =>
  new Binding('contextMenu', OrchestrationDiagramModelProperties.DiagramMode, (mode: OrchestrationDiagramMode) =>
    isOrchestrationDiagramEditable(mode) ? OrchestrationNodeContextMenu() : null,
  ).ofModel();

export const nodeStrokeBinding = () =>
  new Binding('stroke', '', (object: GraphObject, part: go.Part) => {
    const node = object.part as Node;
    const nodeData = (node?.data || {}) as OrchestrationUINode;
    const hasDarkMode = hasDiagramDarkMode(part.diagram);

    if (node.isHighlighted) {
      return hasDarkMode
        ? orchestrationDiagramConfig.node.colors.dark.highlighted
        : orchestrationDiagramConfig.node.colors.light.highlighted;
    }

    if (nodeData?.[OrchestrationNodeBindings.Active]) {
      return orchestrationDiagramConfig.node.colors.active;
    }

    if (node.isSelected) {
      return orchestrationDiagramConfig.colors.primary;
    }

    if (nodeData?.[OrchestrationNodeBindings.DeletedSource]) {
      return hasDarkMode
        ? orchestrationDiagramConfig.node.colors.dark.deleted
        : orchestrationDiagramConfig.node.colors.light.deleted;
    }

    if (nodeData?.[OrchestrationNodeBindings.Duplicate]) {
      return orchestrationDiagramConfig.node.colors.duplicate;
    }

    if (nodeData?.[OrchestrationNodeBindings.Filtered]) {
      const status = nodeData[OrchestrationNodeBindings.JobStatus] as DatasourceTableJobStatus;

      const nodeStatusToColors: Record<JobStatus, string> = {
        [JobStatus.Pending]: orchestrationDiagramConfig.node.colors.statusFiltered.pending,
        [JobStatus.Ready]: orchestrationDiagramConfig.node.colors.statusFiltered.valid,
        [JobStatus.Invalid]: orchestrationDiagramConfig.node.colors.statusFiltered.invalid,
        [JobStatus.Canceled]: orchestrationDiagramConfig.node.colors.statusFiltered.invalid,
        [JobStatus.Expired]: '',
        [JobStatus.InvalidPartial]: '',
        [JobStatus.New]: '',
        [JobStatus.PartiallyEmpty]: '',
        [JobStatus.PartiallyReady]: '',
        [JobStatus.Queued]: '',
      };

      return nodeStatusToColors[status];
    }
    return hasDarkMode
      ? orchestrationDiagramConfig.node.colors.dark.default
      : orchestrationDiagramConfig.node.colors.light.default;
  }).ofObject();

export const dsTextBinding = () =>
  new Binding('text', '', (node: OrchestrationUINode, part: Part) => {
    const derivedNode = getOrchestrationDerivedNode(node, part.diagram!);
    return derivedNode?.entityName || getDeletedEntityName(node.path);
  });

export const dsTextColorBinding = () =>
  new Binding('stroke', '', (node: OrchestrationUINode, part: Part) => {
    const hasDarkMode = hasDiagramDarkMode(part.diagram);

    return node[OrchestrationNodeBindings.DeletedSource]
      ? hasDarkMode
        ? orchestrationDiagramConfig.font.color.disabledDark
        : orchestrationDiagramConfig.font.color.disabledLight
      : hasDarkMode
      ? orchestrationDiagramConfig.font.color.dark
      : orchestrationDiagramConfig.font.color.light;
  });

export const tableTextBinding = () =>
  new Binding('text', '', (node: OrchestrationUINode, part: Part) => {
    const derivedNode = getOrchestrationDerivedNode(node, part.diagram!);
    return derivedNode?.subEntityName || getDeletedEntityName(node.path, true);
  });

export const dsLogoBinding = () =>
  new Binding('source', '', (node: OrchestrationUINode, shape: Part) => {
    const derivedNode = getOrchestrationDerivedNode(node, shape.diagram!);
    return derivedNode?.typeLogo || deletedEntityIcon;
  });

export const subEntryLogoBinding = () =>
  new Binding('source', '', (node: OrchestrationUINode, shape: Part) => {
    const derivedNode = getOrchestrationDerivedNode(node, shape.diagram!);
    return derivedNode?.subEntityLogo || subEntityDefaultLogo(node);
  });

const subEntityDefaultLogo = (node: OrchestrationUINode) => {
  if (node.path.startsWith('src/Exports')) {
    return Y42FeatureTypeToChildLogo[Y42FeatureType.Export];
  }
  return Y42FeatureTypeToChildLogo[Y42FeatureType.Integration];
};

export const visibleInOverview = () =>
  new Binding('visible', '', (node: OrchestrationUINode, part) => {
    const diagramMode = getOrchestrationDiagramModelProperty<OrchestrationDiagramMode>(
      OrchestrationDiagramModelProperties.DiagramMode,
      part.diagram,
    );
    return !node[OrchestrationNodeBindings.DeletedSource] && diagramMode === 'view';
  });

export const visibleInStructure = () =>
  new Binding('visible', '', (node: OrchestrationUINode, part) => {
    const diagramMode = getOrchestrationDiagramModelProperty<OrchestrationDiagramMode>(
      OrchestrationDiagramModelProperties.DiagramMode,
      part.diagram,
    );
    return !node[OrchestrationNodeBindings.DeletedSource] && diagramMode === 'edit';
  });

export const nodeToolTipVisibilityBinding = () =>
  new Binding('visible', '', (node: OrchestrationUINode, part: Part) => {
    const derivedNode = getOrchestrationDerivedNode(node, part.diagram!);
    const name = derivedNode?.entityName || '';
    return node.duplicate || name.length > orchestrationDiagramConfig.node.textLength;
  });

export const importTypeButtonVisibilityBinding = () =>
  new Binding('visible', '', (node: OrchestrationUINode, part) => {
    const derivedNode = getOrchestrationDerivedNode(node, part.diagram);
    return derivedNode && NodesWithIncrementalImport.includes(derivedNode.nodeType);
  });

export const onFailureButtonVisibilityBinding = () =>
  new Binding('visible', '', (node: OrchestrationUINode, part) => {
    const derivedNode = getOrchestrationDerivedNode(node, part.diagram);
    return !!derivedNode;
  });

const getDeletedEntityName = (path: string, childName = false) => {
  const parts = path.split('/');
  if (path.startsWith('src') && parts.length >= 4) {
    return childName ? parts[3] : parts[2];
  }
  return path;
};

export const showLogsButtonVisibilityBinding = () =>
  new Binding('visible', '', (node: OrchestrationUINode, part) => {
    const diagramMode = getOrchestrationDiagramModelProperty<OrchestrationDiagramMode>(
      OrchestrationDiagramModelProperties.DiagramMode,
      part.diagram,
    );
    return !!node[OrchestrationNodeBindings.JobId] && diagramMode === 'view';
  });
